import { useEffect, useState, Fragment } from 'react';
import { Shield, X } from 'lucide-react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '../lib/cn';
import { metaApi } from '../services/annotatorApi';

/**
 * LicenseModal — renders the deployment-wide licence as Markdown.
 *
 * Wire shape returned by `GET /api/v1/org/{orgId}/license`:
 *
 *   {
 *     format:   "markdown",
 *     markdown: "<licence body, Markdown / GFM>"
 *   }
 *
 * The orgId path segment is shape-only — the backend doesn't read it
 * and the licence does not vary by org. URL kept this way so cookie
 * (annotate.{domain} SPA) and standalone share one client path. On
 * IGNODE the LF proxy skips its per-org permission check for this
 * exact path, so any orgId (including a Portal-side sentinel) works.
 *
 * Default content lives in `internal/license/default.md` inside the
 * annotator binary; it's seeded into `ig_annotation_license` JSONB
 * (single-row table, migration 0011) on first read.
 *
 * Renderer: `react-markdown` + `remark-gfm` for GFM autolinks, tables,
 * task lists. `@tailwindcss/typography`'s `prose` classes carry the
 * visual identity — h1 / h2 / blockquote / lists get consistent styling
 * without per-element rules in this file.
 *
 * Read-only here. Editing happens in IGNODE Portal → Platform Controls →
 * Label Factory → "License" card (triple-gated by IGNODE_MODE +
 * RequirePlatformAdmin + a baked-in secret hash).
 */
interface LicenseModalProps {
    open: boolean;
    onClose: () => void;
    orgId?: string | null;
    isDarkMode?: boolean;
}

interface LicensePayload {
    format?: string;
    markdown?: string;
    // Legacy structured shape — pre-0011 rows may still carry the old
    // JSON; we serialise + show in a code block so the modal never
    // crashes. Admins migrate by re-saving via Platform Controls.
    [key: string]: unknown;
}

export default function LicenseModal({ open, onClose, orgId, isDarkMode = false }: LicenseModalProps) {
    const { t } = useTranslation('modals');
    const { t: tCommon } = useTranslation('common');
    const { t: tErrors } = useTranslation('errors');

    const [payload, setPayload] = useState<LicensePayload | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await metaApi.license(orgId ?? undefined);
                if (cancelled) return;
                setPayload(data as LicensePayload);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : tErrors('generic'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, orgId, tErrors]);

    // Pick markdown source — envelope's `markdown` first, legacy JSON
    // fallback wrapped in a code fence so the modal never blanks.
    const markdownSource = (() => {
        if (!payload) return '';
        if (payload.format === 'markdown' && typeof payload.markdown === 'string') {
            return payload.markdown;
        }
        try {
            return '```json\n' + JSON.stringify(payload, null, 2) + '\n```';
        } catch {
            return '';
        }
    })();

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                className={cn(
                                    'w-full max-w-3xl transform overflow-hidden rounded-xl shadow-2xl transition-all',
                                    isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900',
                                )}
                            >
                                {/* Gradient header — same colour family as the
                                    original LicenseModal so the visual entry
                                    point stays recognisable. */}
                                <div
                                    className={cn(
                                        'flex items-start justify-between gap-4 px-6 py-5 border-b',
                                        isDarkMode
                                            ? 'bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border-gray-800'
                                            : 'bg-gradient-to-r from-cyan-50 to-blue-50 border-gray-200',
                                    )}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div
                                            className={cn(
                                                'p-2 rounded-lg flex-shrink-0',
                                                isDarkMode
                                                    ? 'bg-cyan-500/15 text-cyan-300'
                                                    : 'bg-cyan-100 text-cyan-700',
                                            )}
                                        >
                                            <Shield className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0">
                                            <h2 className={cn(
                                                'text-lg font-semibold truncate',
                                                isDarkMode ? 'text-white' : 'text-gray-900',
                                            )}>
                                                {t('license_title', { defaultValue: 'License Agreement' })}
                                            </h2>
                                            <p className={cn(
                                                'text-xs truncate',
                                                isDarkMode ? 'text-gray-400' : 'text-gray-500',
                                            )}>
                                                {t('license_subtitle', { defaultValue: 'IGNODE Label Factory' })}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className={cn(
                                            'p-1.5 rounded-md transition-colors flex-shrink-0',
                                            isDarkMode
                                                ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200'
                                                : 'hover:bg-white/60 text-gray-500 hover:text-gray-700',
                                        )}
                                        aria-label={tCommon('close', { defaultValue: 'Close' })}
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>

                                {/* Body — Markdown rendered inside a `prose`
                                    container. `prose-sm` for compact reading;
                                    `prose-invert` swaps to dark mode. */}
                                <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
                                    {loading ? (
                                        <div className="flex items-center justify-center py-12">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
                                        </div>
                                    ) : error ? (
                                        <div
                                            className={cn(
                                                'rounded-lg border p-3 text-sm',
                                                isDarkMode
                                                    ? 'border-red-800 bg-red-900/20 text-red-300'
                                                    : 'border-red-200 bg-red-50 text-red-800',
                                            )}
                                        >
                                            {error}
                                        </div>
                                    ) : (
                                        <div
                                            className={cn(
                                                'license-content prose prose-sm max-w-none break-words',
                                                isDarkMode && 'prose-invert',
                                            )}
                                        >
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {markdownSource}
                                            </ReactMarkdown>
                                        </div>
                                    )}
                                </div>

                                {/* Footer — single primary close button. */}
                                <div
                                    className={cn(
                                        'px-6 py-4 border-t flex justify-end',
                                        isDarkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-gray-50',
                                    )}
                                >
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors"
                                    >
                                        {tCommon('close', { defaultValue: 'Close' })}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
